/**
 * ActionPicker Component
 * Card-based action selector with icons for notification deep linking
 */

import clsx from 'clsx';

interface ActionOption {
  id: string;
  label: string;
  icon: string;
  description: string;
}

const ACTION_OPTIONS: ActionOption[] = [
  { id: '', label: '无跳转', icon: '🚫', description: '点击通知不执行任何操作' },
  { id: 'profile', label: '个人资料', icon: '👤', description: '打开个人资料页面' },
  { id: 'lucky-charm', label: '幸运符', icon: '✨', description: '打开幸运符弹窗' },
  { id: 'soulmate', label: 'Soulmate', icon: '❤️', description: '打开 Soulmate 抽屉' },
  { id: 'bookmarks', label: '收藏', icon: '🔖', description: '打开收藏抽屉' },
  { id: 'topics', label: '话题', icon: '💬', description: '打开话题抽屉' },
  { id: 'birth-chart', label: '命盘列表', icon: '🌟', description: '打开命盘列表' },
  { id: 'birth-chart-form', label: '命盘表单', icon: '📝', description: '打开命盘表单' },
  { id: 'memory-list', label: '记忆列表', icon: '🧠', description: '打开记忆列表' },
  { id: 'feedback', label: '反馈', icon: '💭', description: '打开反馈表单' },
  { id: 'font-size', label: '字体设置', icon: '🔤', description: '打开字体设置' },
];

interface ActionPickerProps {
  value: string;
  onChange: (action: string) => void;
  disabled?: boolean;
  className?: string;
}

export function ActionPicker({ value, onChange, disabled, className }: ActionPickerProps) {
  return (
    <div className={clsx('space-y-2', className)}>
      <label className="block text-sm font-medium text-gray-700">
        点击跳转
      </label>

      <div className="grid grid-cols-4 gap-2 sm:grid-cols-5 md:grid-cols-6">
        {ACTION_OPTIONS.map(action => {
          const isSelected = value === action.id;

          return (
            <button
              key={action.id}
              type="button"
              disabled={disabled}
              onClick={() => onChange(action.id)}
              className={clsx(
                'flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all text-center',
                isSelected
                  ? 'border-indigo-500 bg-indigo-50 shadow-sm'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50',
                disabled && 'opacity-60 cursor-not-allowed'
              )}
              title={action.description}
            >
              <span className="text-2xl mb-1" role="img" aria-label={action.label}>
                {action.icon}
              </span>
              <span className={clsx(
                'text-[10px] font-medium leading-tight',
                isSelected ? 'text-indigo-700' : 'text-gray-600'
              )}>
                {action.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Show selected action description */}
      {value && (
        <p className="text-xs text-gray-500 mt-2">
          {ACTION_OPTIONS.find(a => a.id === value)?.description}
        </p>
      )}
    </div>
  );
}

// Compact version for inline use
interface ActionPickerCompactProps {
  value: string;
  onChange: (action: string) => void;
  disabled?: boolean;
  className?: string;
}

export function ActionPickerCompact({ value, onChange, disabled, className }: ActionPickerCompactProps) {
  return (
    <div className={clsx('space-y-2', className)}>
      <label className="block text-sm font-medium text-gray-700">
        点击跳转 Action
      </label>

      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
      >
        {ACTION_OPTIONS.map(action => (
          <option key={action.id} value={action.id}>
            {action.icon} {action.label}
          </option>
        ))}
      </select>
    </div>
  );
}
